import { Button } from "@aurumiq/ui/components/button";
import { Input } from "@aurumiq/ui/components/input";
import { Label } from "@aurumiq/ui/components/label";
import { type FormEvent, useEffect, useState } from "react";
import { Navigate, useLocation } from "react-router";
import { useAuth } from "@/auth/auth-context";
import { exchangeSsoToken } from "@/auth/auth-provider";
import { parseSsoCallback } from "@/auth/sso-config";

export function LoginPage() {
  const auth = useAuth();
  const location = useLocation();
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const from = (location.state as { from?: { pathname?: string } } | null)?.from?.pathname ?? "/";

  // Returning from Microsoft: GoTrue puts the session in the URL fragment.
  useEffect(() => {
    const cb = parseSsoCallback(window.location.hash);
    if (!cb) return;
    window.history.replaceState(null, "", window.location.pathname);
    if ("error" in cb) {
      setError(`Microsoft sign-in failed: ${cb.error}`);
      return;
    }
    setBusy(true);
    exchangeSsoToken(cb.accessToken)
      .catch((err) => setError(err instanceof Error ? err.message : "Sign-in failed."))
      .finally(() => setBusy(false));
  }, []);

  if (auth.ready && auth.isAuthenticated) return <Navigate to={from} replace />;

  const submit = async (e: FormEvent) => {
    e.preventDefault();
    setBusy(true);
    setError(null);
    try {
      await auth.signInWithPassword(email.trim(), password);
    } catch (err) {
      setError(err instanceof Error ? err.message : "Sign-in failed.");
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="grid min-h-screen place-items-center bg-navy p-4">
      <div className="w-full max-w-sm rounded-2xl bg-card p-8 shadow-lg">
        <div className="text-center font-bold text-2xl">
          Aurum<b className="text-gold-fg">IQ</b> Admin
        </div>
        <p className="mt-1 text-center text-muted-foreground text-sm">Staff sign-in</p>

        <form onSubmit={submit} className="mt-6 space-y-3" noValidate>
          <div>
            <Label htmlFor="email">Email</Label>
            <Input
              id="email"
              type="email"
              autoComplete="username"
              required
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              disabled={busy}
            />
          </div>
          <div>
            <Label htmlFor="password">Password</Label>
            <Input
              id="password"
              type="password"
              autoComplete="current-password"
              required
              value={password}
              onChange={(e) => setPassword(e.target.value)}
              disabled={busy}
            />
          </div>
          {error ? (
            <p role="alert" className="rounded-md bg-danger-bg px-3 py-2 text-destructive text-sm">
              {error}
            </p>
          ) : null}
          <Button type="submit" className="w-full" disabled={busy || !email || !password}>
            {busy ? "Signing in…" : "Sign in"}
          </Button>
        </form>

        {auth.signIn ? (
          <>
            <div className="my-4 flex items-center gap-3 text-muted-foreground text-xs">
              <span className="h-px flex-1 bg-border" />
              or
              <span className="h-px flex-1 bg-border" />
            </div>
            <Button
              variant="outline"
              className="w-full"
              onClick={() => auth.signIn?.()}
              disabled={!auth.ready}
            >
              Sign in with Microsoft
            </Button>
          </>
        ) : null}
        {auth.mode === "test" ? (
          <p className="mt-4 text-center text-muted-foreground text-xs">
            Test auth mode is on — you are already signed in.
          </p>
        ) : null}
      </div>
    </div>
  );
}
